import { z } from 'zod';
import { PERIOD_PRESETS } from './periods.js';

const optionalText = z.string().trim().min(1).max(200).optional();
const optionalId = z.string().trim().min(1).max(64).optional();

/**
 * Every filter a report may take, validated once. A report definition lists
 * which of these it supports and the engine rejects the others, so a filter
 * can never be silently ignored.
 */
export const financeReportFilterSchema = z
  .object({
    preset: z.enum(PERIOD_PRESETS).optional(),
    from: z.iso.date().optional(),
    to: z.iso.date().optional(),
    asOf: z.iso.date().optional(),
    customerId: optionalId,
    vendorId: optionalId,
    itemName: optionalText,
    itemListId: optionalId,
    invoiceType: z.enum(['NORMAL', 'GST', 'ALL']).optional(),
    status: optionalText,
    paymentStatus: z.enum(['PAID', 'UNPAID', 'PARTIAL', 'OVERDUE']).optional(),
    expenseCategory: optionalText,
    expenseReportStatus: optionalText,
    approverId: optionalId,
    cashOfficerId: optionalId,
    reimbursementStatus: z.enum(['REIMBURSED', 'PENDING']).optional(),
    bankAccountId: optionalId,
    ledgerAccountId: optionalId,
    accountType: optionalText,
    currency: z.string().trim().length(3).toUpperCase().optional(),
    salesPersonId: optionalId,
    claimantId: optionalId,
    transactionType: optionalText,
    taxRate: z.coerce.number().min(0).max(100).optional(),
    entityType: optionalText,
    actorId: optionalId,
    search: z.string().trim().max(100).optional(),
    groupBy: optionalText,
    compare: z.enum(['previous_period', 'previous_year']).optional(),
    ageInterval: z.coerce.number().int().min(1).max(365).optional(),
    includeAdjustmentPeriod: z.stringbool().optional(),
  })
  .strict();
export type FinanceReportFilters = z.infer<typeof financeReportFilterSchema>;

/** Presentation options every report accepts, whatever its filters. */
export const financeReportViewSchema = z.object({
  /** Comma-separated column keys to show, in order. */
  columns: z.string().trim().max(500).optional(),
  sortBy: z.string().trim().max(60).optional(),
  sortDir: z.enum(['asc', 'desc']).optional(),
  page: z.coerce.number().int().min(1).max(100000).optional(),
  pageSize: z.coerce.number().int().min(1).max(500).optional(),
});
export type FinanceReportView = z.infer<typeof financeReportViewSchema>;

/** Maps each filter key in the catalogue to the query parameters it owns. */
export const FINANCE_FILTER_PARAMS: Record<string, string[]> = {
  period: ['preset', 'from', 'to'],
  asOf: ['asOf'],
  customerId: ['customerId'],
  vendorId: ['vendorId'],
  itemName: ['itemName'],
  itemListId: ['itemListId'],
  invoiceType: ['invoiceType'],
  status: ['status'],
  paymentStatus: ['paymentStatus'],
  expenseCategory: ['expenseCategory'],
  expenseReportStatus: ['expenseReportStatus'],
  approverId: ['approverId'],
  cashOfficerId: ['cashOfficerId'],
  reimbursementStatus: ['reimbursementStatus'],
  bankAccountId: ['bankAccountId'],
  ledgerAccountId: ['ledgerAccountId'],
  accountType: ['accountType'],
  currency: ['currency'],
  salesPersonId: ['salesPersonId'],
  claimantId: ['claimantId'],
  transactionType: ['transactionType'],
  taxRate: ['taxRate'],
  entityType: ['entityType'],
  actorId: ['actorId'],
  search: ['search'],
  groupBy: ['groupBy'],
  compare: ['compare'],
  ageInterval: ['ageInterval'],
  includeAdjustmentPeriod: ['includeAdjustmentPeriod'],
};

// ---- results ----

export type FinanceReportCell = string | number | null | undefined;

export interface FinanceReportRow {
  [key: string]: FinanceReportCell | FinanceReportLink | undefined;
  _kind?: 'row' | 'group' | 'subtotal' | 'total' | 'heading';
  _depth?: number;
  _link?: FinanceReportLink;
}

/** A pointer to the record behind a row; the client maps `type` to its page. */
export interface FinanceReportLink {
  type: string;
  id: string;
}

export interface FinanceReportColumn {
  key: string;
  label: string;
  type: 'text' | 'money' | 'number' | 'percent' | 'date' | 'status';
}

export interface FinanceReportResult {
  reportId: string;
  title: string;
  generatedAt: string;
  baseCurrency: string;
  /** What was asked for, in words, for the page and for exports. */
  filtersSummary: string[];
  period: { from: string; to: string } | { asOf: string } | null;
  comparePeriod: { from: string; to: string } | null;
  columns: FinanceReportColumn[];
  rows: FinanceReportRow[];
  totals: FinanceReportRow | null;
  notes: string[];
  chart: { type: 'bar' | 'pie' | 'line'; labelKey: string; valueKeys: string[] } | null;
  pagination: { page: number; pageSize: number; total: number } | null;
  orientation: 'portrait' | 'landscape';
}
